import { NotificationCategory, NotificationService } from '@splunk/olly-common';
import Badge from '@splunk/react-ui/Badge';
import Button from '@splunk/react-ui/Button';
import Card from '@splunk/react-ui/Card';
import Heading from '@splunk/react-ui/Heading';
import { variables } from '@splunk/themes';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { AngularInjector } from '../../../common/AngularUtils';
import { ThemeProvider, useLegacyThemeServiceKey } from '../../../common/theme/ThemeProvider';
import { getApiHost } from '../utils/getApiHost';
import { ModernDashboardsCardImage } from './ModernDashboardsCardImage';

export default function ModernDashboardsCard(): JSX.Element {
    const themeKey = useLegacyThemeServiceKey();
    const currentUser = AngularInjector.useInjectedClass('currentUser');

    const [isAdmin, setIsAdmin] = useState(false);

    const [migrationInProgress, setMigrationInProgress] = useState(false);
    const [migrationStatus, setMigrationStatus] = useState<MigrationStatus | undefined>();

    useEffect(() => {
        void currentUser.isAdmin().then(setIsAdmin);
    }, [currentUser]);

    useEffect(() => {
        void (async (): Promise<void> => {
            const response = await fetch(new URL('/v2/directory/~migrate/status', getApiHost()), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-SF-TOKEN': window.localStorage.getItem('X-SF-TOKEN') ?? '',
                },
            });

            if (!response.ok) {
                return;
            }

            try {
                const json = (await response.json()) as {
                    data: { status: MigrationStatus };
                };

                if (json.data.status === 'MIGRATED') {
                    window.location.hash = '#/modern-dashboards';
                }

                setMigrationStatus(json.data.status);
            } catch {
                setMigrationStatus('UNMIGRATED');
            }
        })();
    }, []);

    const migrateCallback = useCallback(() => {
        migrate(setMigrationInProgress);
    }, [setMigrationInProgress]);

    return (
        <ThemeProvider colorScheme={themeKey}>
            <StyledCard>
                <Card.Body>
                    <StyledContent>
                        <StyledImage src={ModernDashboardsCardImage} />
                        <StyledMessage>
                            <StyledHeading level={2}>
                                Introducing the new dashboard experience{' '}
                                <StyledBadge
                                    backgroundColor={variables.notificationColorInfoWeak}
                                    label="New"
                                />
                            </StyledHeading>
                            <StyledMessageContent>
                                The new dashboards offer flexible layouts, more visualization types,
                                and broader data source support to help you visualize key metrics
                                across your organization.
                            </StyledMessageContent>
                            <StyledActions>
                                <StyledSwitchButton
                                    appearance="primary"
                                    to={'#/modern-dashboards/'}
                                >
                                    <StyledViewDashboardsText>
                                        View new dashboards
                                    </StyledViewDashboardsText>
                                </StyledSwitchButton>
                                {isAdmin && (
                                    <StyledMigrationActions>
                                        <Button
                                            appearance="primary"
                                            disabled={
                                                migrationInProgress ||
                                                migrationStatus === 'MIGRATING' ||
                                                migrationStatus === 'MIGRATED' ||
                                                migrationStatus === undefined
                                            }
                                            onClick={migrateCallback}
                                        >
                                            Migrate dashboards
                                        </Button>
                                    </StyledMigrationActions>
                                )}
                            </StyledActions>
                        </StyledMessage>
                    </StyledContent>
                </Card.Body>
            </StyledCard>
        </ThemeProvider>
    );
}

async function migrate(setMigrationInProgress: Dispatch<SetStateAction<boolean>>): Promise<void> {
    setMigrationInProgress(true);

    const response = await fetch(new URL('/v2/directory/~command', getApiHost()), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-SF-TOKEN': localStorage['X-SF-TOKEN'],
        },
        body: JSON.stringify({
            type: 'https://schema.splunkdev.com/dashify/v1/directory/command/MigrateSignalview',
        }),
    });

    if (response.status >= 500) {
        NotificationService.post({
            category: NotificationCategory.TOAST,
            type: 'error',
            message: `${response.status} ${response.statusText}`,
        });

        setMigrationInProgress(false);
    } else if (response.status >= 400) {
        let envelope: ErrorEnvelope | undefined;
        try {
            envelope = (await response.json()) as ErrorEnvelope;
        } catch {}

        NotificationService.post({
            category: NotificationCategory.TOAST,
            type: 'error',
            message:
                envelope?.errors?.at(0)?.message ?? `${response.status} ${response.statusText}`,
        });

        setMigrationInProgress(false);
    } else {
        NotificationService.post({
            category: NotificationCategory.TOAST,
            type: 'success',
            message: 'Started migration',
        });
    }
}

interface ErrorInfo {
    readonly code: string;
    readonly message: string;
}

interface ErrorEnvelope {
    readonly data: null;
    readonly errors: readonly ErrorInfo[];
}

type MigrationStatus = 'MIGRATED' | 'MIGRATING' | 'UNMIGRATED';

const StyledCard = styled(Card)`
    margin-inline: ${variables.spacingLarge};
`;

const StyledContent = styled.div`
    display: flex;
    gap: ${variables.spacingXXLarge};
`;

const StyledImage = styled.img`
    width: 350px;
    height: 222px;
`;

const StyledMessage = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${variables.spacingSmall};
`;

// Override bootstrap styles.
const StyledHeading = styled(Heading)`
    align-items: center;
    display: flex;
    margin: 0 !important;
    line-height: ${variables.lineHeightSingle} !important;
    font-size: ${variables.fontSizeXXLarge} !important;
    font-weight: ${variables.fontWeightBold} !important;
`;

const StyledBadge = styled(Badge)`
    flex: 0 0 auto;
    margin-inline: ${variables.spacingSmall};
`;

const StyledMessageContent = styled.div`
    flex: 1;
`;

const StyledActions = styled.div`
    display: flex;
    gap: ${variables.spacingSmall};
    justify-content: space-between;
`;

const StyledSwitchButton = styled(Button)`
    flex: none;
`;

const StyledMigrationActions = styled.div`
    display: flex;
    gap: ${variables.spacingSmall};
`;

// Required since signalview breaks primary button text color in dark mode.
const StyledViewDashboardsText = styled.span`
    color: ${variables.contentColorInverted};
`;
